import { Tool } from '../types';

const PLACEHOLDER_LOGO = '/assets/placeholder-logo.svg';

function hostnameFromUrl(websiteUrl?: string | null): string | null {
  if (!websiteUrl) return null;
  try {
    return new URL(websiteUrl).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function firstStoredLogo(tool: Tool): string {
  return String(tool.logo_url || tool.logo || tool.logo_source_url || '');
}

/** Prefer a PNG sibling for local tool-logos ICOs (cards render PNG more reliably). */
export function preferCardLogoUrl(url: string): string {
  if (/^\/tool-logos\/.+\.ico$/i.test(url)) {
    return url.replace(/\.ico$/i, '.png');
  }
  return url;
}

/** Prefer local/official logo fields; fall back to a favicon that works in CN better than Clearbit. */
export function getToolLogoUrl(tool: Tool): string {
  const pick = (url?: string | null) =>
    url && !isPlaceholderLogoUrl(url) ? preferCardLogoUrl(url) : null;

  const stored =
    pick(tool.logo_url) ||
    pick(tool.logo) ||
    pick(tool.logo_source_url);
  if (stored) return stored;

  // Never use Google favicons as primary — often blocked in CN and slow elsewhere.
  return PLACEHOLDER_LOGO;
}

/** Original stored logo URL (for onError fallback when PNG sibling is missing). */
export function getToolLogoFallbackUrl(tool: Tool): string {
  if (tool.logo_url && !isPlaceholderLogoUrl(tool.logo_url)) return tool.logo_url;
  if (tool.logo && !isPlaceholderLogoUrl(tool.logo)) return tool.logo;
  if (tool.logo_source_url && !isPlaceholderLogoUrl(tool.logo_source_url)) return tool.logo_source_url;
  return PLACEHOLDER_LOGO;
}

export function isPlaceholderLogoUrl(url?: string | null): boolean {
  if (!url) return true;
  const u = String(url);
  return (
    u.includes('placeholder-logo') ||
    u.includes('logo.clearbit.com') ||
    u.includes('google.com/s2/favicons') ||
    u.includes('icons.duckduckgo.com') ||
    u.endsWith('/assets/placeholder-logo.svg') ||
    /\/tool-logos\/-+\./i.test(u) ||
    /\/tool-logos\/-\./i.test(u)
  );
}

/** True when the tool has a stored logo (not favicon/placeholder fallback). */
export function hasStoredToolLogo(tool: Tool): boolean {
  return !!(
    (tool.logo_url && !isPlaceholderLogoUrl(tool.logo_url)) ||
    (tool.logo && !isPlaceholderLogoUrl(tool.logo)) ||
    (tool.logo_source_url && !isPlaceholderLogoUrl(tool.logo_source_url))
  );
}

/**
 * Card cover URL — only real product screenshots / external showcase images.
 * Never use /tool-logos/* here: those are logos (often tiny favicons) and look
 * broken when stretched as 16:9 covers. Without a real shot, ToolCard shows a
 * centered logo on a clean background instead.
 */
export function getToolShowcaseUrl(tool: Tool): string | null {
  const storedLogo = firstStoredLogo(tool);

  const candidates = [
    ...(tool.screenshot_urls || []),
    ...(tool.screenshot_source_urls || []),
    tool.showcase_image,
  ].filter(Boolean) as string[];

  for (const url of candidates) {
    if (!url || url === storedLogo) continue;
    if (/\.ico(\?|$)/i.test(url) || /favicon/i.test(url)) continue;
    if (/^\/tool-logos\//i.test(url)) continue;
    if (isPlaceholderLogoUrl(url)) continue;
    return url;
  }
  return null;
}

export function getToolScreenshots(tool: Tool): string[] {
  if (tool.screenshot_urls && tool.screenshot_urls.length > 0) {
    return tool.screenshot_urls;
  }
  if (tool.showcase_image) {
    return [tool.showcase_image];
  }
  if (tool.screenshot_source_urls && tool.screenshot_source_urls.length > 0) {
    return tool.screenshot_source_urls;
  }
  return [];
}

export function isPlaceholderTool(tool: Tool): boolean {
  return !!(tool.asset_placeholder_logo || tool.asset_placeholder_screenshots);
}
